import React, { useEffect, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { unwrapList } from './supplierAssessmentUi';

const { Option } = Select;
const { TextArea } = Input;

const LIST_PATH = '/scm/quality/supplier-assessments';

interface AssessmentTemplate {
  id: string;
  template_name: string;
  version: string;
  is_active?: boolean;
}

interface SupplierOption {
  id: string;
  supplier_name: string;
  supplier_code?: string;
}

/**
 * Full-page create for Supplier Assessment (replaces list-page Modal).
 * Same capability / API as before — no new permission codes.
 */
const CreateSupplierAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState<AssessmentTemplate[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingOptions(true);
      try {
        const [tplRes, supRes] = await Promise.all([
          apiClient.get('/procurement/supplier-assessment-templates'),
          apiClient.get('/procurement/suppliers', { params: { is_active: 'true' } }),
        ]);
        if (cancelled) return;
        setTemplates(unwrapList<AssessmentTemplate>(tplRes).filter((t) => t.is_active !== false));
        setSuppliers(unwrapList<SupplierOption>(supRes));
      } catch {
        if (!cancelled) message.error('Failed to load suppliers or templates');
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [message]);

  const handleCreate = async (values: Record<string, any>) => {
    setSubmitting(true);
    try {
      const response = await apiClient.post('/procurement/supplier-assessments', {
        supplier_id: values.supplier_id,
        template_id: values.template_id,
        assessment_type: values.assessment_type || 'Initial',
        assessment_date: values.assessment_date.format('YYYY-MM-DD'),
        assessor_name: values.assessor_name,
        assessment_scope: values.assessment_scope || values.scope_of_supply,
        assessment_location: values.assessment_location,
        evaluation_mode: values.evaluation_mode,
        evaluation_reason: values.evaluation_reason,
        supplier_representative: values.supplier_representative,
        customer_or_legal_limited: Boolean(values.customer_or_legal_limited),
        scope_of_supply: values.scope_of_supply || values.assessment_scope,
        grn_reference: values.grn_reference,
        po_reference: values.po_reference,
      });
      message.success('Assessment created successfully');
      const created =
        response && typeof response === 'object' && 'data' in response
          ? (response as { data: any }).data
          : response;
      const createdId = created?.id || created?.data?.id;
      if (createdId) {
        navigate(`${LIST_PATH}/${createdId}`);
        return;
      }
      navigate(LIST_PATH);
    } catch (error: any) {
      const detail =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to create assessment';
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(LIST_PATH)}
        style={{ marginBottom: 16 }}
      >
        Back
      </Button>

      <Card title="Create Supplier Assessment" loading={loadingOptions}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{
            assessment_type: 'Initial',
            assessment_date: dayjs(),
            customer_or_legal_limited: false,
          }}
          style={{ maxWidth: 880 }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="supplier_id"
                label="Supplier"
                rules={[{ required: true, message: 'Please select supplier' }]}
              >
                <Select
                  placeholder="Select Supplier"
                  showSearch
                  optionFilterProp="label"
                  options={suppliers.map((s) => ({
                    value: s.id,
                    label: s.supplier_code
                      ? `${s.supplier_name} (${s.supplier_code})`
                      : s.supplier_name,
                  }))}
                  notFoundContent={suppliers.length === 0 ? 'No active suppliers found' : undefined}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="template_id"
                label="Assessment Template"
                rules={[{ required: true, message: 'Please select template' }]}
              >
                <Select placeholder="Select Template" showSearch optionFilterProp="children">
                  {templates.map((template) => (
                    <Option key={template.id} value={template.id}>
                      {template.template_name} (v{template.version})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="assessment_type"
                label="Assessment Type"
                rules={[{ required: true, message: 'Please select assessment type' }]}
              >
                <Select placeholder="Select type">
                  <Option value="Initial">Initial</Option>
                  <Option value="Re-evaluation">Re-evaluation</Option>
                  <Option value="Special-audit">Special audit</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="assessment_date"
                label="Assessment Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="assessor_name"
                label="Assessor Name"
                rules={[{ required: true, message: 'Please enter assessor name' }]}
              >
                <Input placeholder="Name of assessor" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="evaluation_mode"
            label="Evaluation mode"
            extra="High-risk critical suppliers without onsite should use Remote + Receiving Inspection."
          >
            <Select allowClear placeholder="Onsite / Remote / Remote+RI">
              <Option value="Onsite">Onsite</Option>
              <Option value="Remote">Remote</Option>
              <Option value="Remote+Receiving-Inspection">Remote + Receiving Inspection</Option>
            </Select>
          </Form.Item>

          <Form.Item name="customer_or_legal_limited" label="Customer / legal / proprietary limited?">
            <Radio.Group>
              <Radio value={false}>No</Radio>
              <Radio value={true}>Yes (skips supply-chain section on critical form)</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="supplier_representative" label="Supplier representative">
            <Input placeholder="Name & designation" />
          </Form.Item>

          <Form.Item name="evaluation_reason" label="Reason for evaluation / re-evaluation">
            <TextArea rows={2} />
          </Form.Item>

          <Form.Item name="scope_of_supply" label="Scope of supply">
            <TextArea rows={2} placeholder="Products or services provided" />
          </Form.Item>

          <Form.Item name="assessment_scope" label="Assessment Scope (legacy)">
            <TextArea rows={2} placeholder="Optional additional scope notes..." />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="grn_reference" label="GRN reference (FAI / product assessment)">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="po_reference" label="PO reference">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="assessment_location" label="Location">
            <Input placeholder="Assessment location" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create Assessment
              </Button>
              <Button onClick={() => navigate(LIST_PATH)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateSupplierAssessmentPage;
